<template>
  <div class="ent-page">
    <div class="gs-page-hero">
      <div class="gs-hero-art is-enterprise" aria-hidden="true"></div>
      <div class="gs-page-hero-inner">
        <div>
          <h1>企业资料</h1>
          <p>按企业公开信息检索已发布的企业档案。</p>
        </div>
      </div>
      <div class="gs-page-hero-metrics">
        <div v-for="item in heroStats" :key="item.label" class="gs-page-hero-metric">
          <div class="metric-num"><em>{{ item.value }}</em>{{ item.unit }}</div>
          <div class="metric-label">{{ item.label }}</div>
        </div>
      </div>
    </div>

    <div class="ent-main">
      <div class="ent-filters" :class="{ 'is-collapsed': isNarrow && !filtersOpen }">
        <label class="ent-field is-primary">
          <span>企业名称</span>
          <input v-model.trim="query.enterpriseName" type="text" placeholder="请输入企业名称" @keyup.enter="search">
        </label>
        <label class="ent-field is-primary">
          <span>企业类型</span>
          <GsSelect v-model="query.typeOfEnterprise" class="ent-select" filterable placeholder="请选择" :options="typeOptions" />
        </label>
        <label class="ent-field is-primary">
          <span>所属行业</span>
          <input v-model.trim="query.industry" type="text" placeholder="请输入所属行业" @keyup.enter="search">
        </label>
        <div class="ent-actions is-primary">
          <button type="button" class="ent-btn" @click="search">查询</button>
          <button type="button" class="ent-btn is-plain" @click="reset">重置</button>
          <button
            v-if="isNarrow"
            type="button"
            class="ent-btn is-plain ent-more-btn"
            @click="filtersOpen = !filtersOpen"
          >{{ filtersOpen ? '收起' : '更多筛选' }}</button>
        </div>

        <label class="ent-field is-more">
          <span>企业简称</span>
          <input v-model.trim="query.enterpriseAbbreviation" type="text" placeholder="请输入企业简称" @keyup.enter="search">
        </label>
        <label class="ent-field is-more">
          <span>信用代码</span>
          <input v-model.trim="query.unifySocialCreditCode" type="text" placeholder="统一社会信用代码" @keyup.enter="search">
        </label>
        <label class="ent-field is-more">
          <span>英文名称</span>
          <input v-model.trim="query.englishName" type="text" placeholder="请输入英文名称" @keyup.enter="search">
        </label>
        <label class="ent-field is-more">
          <span>企业属性</span>
          <GsSelect v-model="query.type" class="ent-select" filterable placeholder="请选择" :options="attrOptions" />
        </label>
        <label class="ent-field is-more">
          <span>行政区划</span>
          <GsRegionSelect
            v-model="regionPath"
            class="ent-select"
            placeholder="请选择省 / 市 / 区"
            @change="onRegionChange"
          />
        </label>
        <label class="ent-field is-more">
          <span>联系电话</span>
          <input v-model.trim="query.phone" type="text" placeholder="请输入电话" @keyup.enter="search">
        </label>
        <label class="ent-field is-more">
          <span>注册地址</span>
          <input v-model.trim="query.registeredAddress" type="text" placeholder="请输入注册地址" @keyup.enter="search">
        </label>
        <label class="ent-field ent-dates is-more">
          <span>发布时间</span>
          <GsDate v-model="query.startTime" />
          <em>至</em>
          <GsDate v-model="query.endTime" />
        </label>
      </div>

      <div v-loading="loading" class="ent-table-wrap">
        <div class="ent-table">
          <div class="ent-head">
            <span>企业名称</span>
            <span>企业属性</span>
            <span>所属行业</span>
            <span>行政区划</span>
            <span>联系电话</span>
            <span>操作</span>
          </div>
          <div v-if="rows.length" class="ent-body">
            <div v-for="row in rows" :key="row.id || row.enterpriseName" class="ent-row">
              <span class="ent-name">{{ row.enterpriseName || '—' }}</span>
              <span data-label="企业属性" :title="typeText(row)">{{ typeText(row) }}</span>
              <span data-label="所属行业" :title="industryText(row)">{{ industryText(row) }}</span>
              <span data-label="行政区划" :title="labelOf(regionOptions, row.administrativeDivision)">{{ labelOf(regionOptions, row.administrativeDivision) }}</span>
              <span data-label="联系电话" :title="row.phone">{{ row.phone || '—' }}</span>
              <span class="ent-act">
                <button type="button" @click="openDetail(row)">详情</button>
              </span>
            </div>
          </div>
          <NoData v-else />
        </div>
        <div v-if="total" class="ent-pager">
          <GsPagination
            :current-page="currentPage"
            :page-size="pageSize"
            :total="total"
            @current-change="onPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getEnterpriseList } from '@/api/api.js'
import { getDataType, regionList } from '@/api/Layout/index.js'
import { enumType } from '@/utils/enum.js'
import { P } from '@/router/paths'
import { findNode } from '@/utils/region'
import NoData from '@/components/NoData/index.vue'
import GsPagination from '@/components/GsPagination/index.vue'

function blankQuery() {
  return {
    enterpriseName: '',
    enterpriseAbbreviation: '',
    unifySocialCreditCode: '',
    englishName: '',
    typeOfEnterprise: '',
    type: '',
    industry: '',
    administrativeDivision: '',
    phone: '',
    registeredAddress: '',
    startTime: '',
    endTime: ''
  }
}

export default {
  name: 'EnterpriseArchive',
  components: { NoData, GsPagination },
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      query: blankQuery(),
      typeOptions: [],
      attrOptions: [],
      regionOptions: [],
      regionTree: [],
      regionPath: [],
      filtersOpen: false,
      isNarrow: typeof window !== 'undefined' && window.innerWidth < 960,
      heroStats: [
        { label: '已发布企业', value: '-', unit: '家' },
        { label: '数据交易所', value: '-', unit: '家' },
        { label: '数据商服务商', value: '-', unit: '家' },
        { label: '其他企业', value: '-', unit: '家' },
        { label: '覆盖区划', value: '-', unit: '个' },
        { label: '已填行业', value: '-', unit: '家' }
      ]
    }
  },
  created() {
    this.syncNarrow()
    this.loadDicts().finally(() => this.loadHero())
    this.loadList()
  },
  mounted() {
    window.addEventListener('resize', this.syncNarrow)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.syncNarrow)
  },
  methods: {
    syncNarrow() {
      this.isNarrow = typeof window !== 'undefined' && window.innerWidth < 960
      if (!this.isNarrow) this.filtersOpen = false
    },
    loadDicts() {
      const typeTask = getDataType('sys_enterprise_type').then((res) => {
        this.typeOptions = res && res.data ? (enumType(res.data) || []) : []
      }).catch(() => { this.typeOptions = [] })
      const attrTask = getDataType('sys_type').then((res) => {
        this.attrOptions = res && res.data ? (enumType(res.data) || []) : []
      }).catch(() => { this.attrOptions = [] })
      const regionTask = regionList().then((res) => {
        const tree = (res && res.data) || []
        this.regionTree = tree
        this.regionOptions = this.flattenRegions(tree)
      }).catch(() => { this.regionTree = []; this.regionOptions = [] })
      return Promise.all([typeTask, attrTask, regionTask])
    },
    /** 三级联动选择：只把末级行政区划代码作为筛选条件 */
    onRegionChange(path) {
      const list = Array.isArray(path) ? path : []
      this.regionPath = list
      const leaf = list.length ? findNode(this.regionTree, list[list.length - 1]) : null
      this.query.administrativeDivision = leaf ? String(leaf.value) : ''
      this.search()
    },
    flattenRegions(list, acc) {
      const out = acc || []
      ;(list || []).forEach((item) => {
        if (item && item.value != null && item.label) out.push({ value: item.value, label: item.label })
        if (item && item.children && item.children.length) this.flattenRegions(item.children, out)
      })
      return out
    },
    typeText(row) {
      if (!row) return '—'
      if (row.typeOfEnterprise) return this.labelOf(this.typeOptions, row.typeOfEnterprise)
      if (row.type != null && row.type !== '') return this.labelOf(this.attrOptions, row.type)
      return '—'
    },
    labelOf(list, value) {
      if (value == null || value === '') return '—'
      const hit = (list || []).find((item) => String(item.value) === String(value))
      return hit ? hit.label : String(value)
    },
    industryText(row) {
      const raw = row && row.industry
      if (!raw) return '—'
      try {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (Array.isArray(data)) {
          const text = data.map((item) => (item && (item.label || item.name)) || item).filter(Boolean).join(' / ')
          return text || '—'
        }
      } catch (error) {
        return String(raw)
      }
      return String(raw)
    },
    search() {
      this.currentPage = 1
      this.loadList()
    },
    reset() {
      this.query = blankQuery()
      this.regionPath = []
      this.search()
    },
    onPage(page) {
      this.currentPage = page
      this.loadList()
    },
    loadHero() {
      getEnterpriseList({ pageNum: 1, pageSize: 200, whetherPublish: 1 }).then((res) => {
        const rows = (res && res.rows) || []
        const total = (res && res.total) || rows.length
        const labels = {}
        ;(this.attrOptions || []).forEach((item) => { labels[String(item.value)] = item.label })
        const buckets = {}
        const regions = new Set()
        let industry = 0
        rows.forEach((row) => {
          const key = row && row.type != null && row.type !== '' ? String(row.type) : ''
          if (key) buckets[key] = (buckets[key] || 0) + 1
          const region = row && (row.administrativeDivisionName || row.registeredAddressName)
          if (region) regions.add(region)
          if (row && row.industry) industry += 1
        })
        const known = Object.keys(labels)
        let knownSum = 0
        const typed = known.map((key) => {
          const n = buckets[key] || 0
          knownSum += n
          return { label: labels[key], value: n, unit: '家' }
        }).filter((item) => item.value > 0)
        const other = Math.max(0, total - knownSum)
        const cards = [{ label: '已发布企业', value: total, unit: '家' }].concat(typed)
        if (other) cards.push({ label: '其他企业', value: other, unit: '家' })
        cards.push({ label: '覆盖区划', value: regions.size, unit: '个' })
        cards.push({ label: '已填行业', value: industry, unit: '家' })
        this.heroStats = cards.slice(0, 6)
      }).catch(() => {})
    },
    loadList() {
      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        whetherPublish: 1
      }
      Object.keys(this.query).forEach((key) => {
        const val = this.query[key]
        if (val !== '' && val != null) params[key] = val
      })
      this.loading = true
      getEnterpriseList(params).then((res) => {
        this.rows = (res && res.rows) || []
        this.total = (res && res.total) || 0
      }).catch(() => {
        this.rows = []
        this.total = 0
      }).finally(() => {
        this.loading = false
      })
    },
    openDetail(row) {
      const id = row && (row.id || row.enterpriseId)
      if (!id) return
      this.$router.push(P.enterprise(id)).catch(() => false)
    }
  }
}
</script>

<style scoped>
.ent-title {
  position: relative;
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(50% - 50vw);
  background: #0165FF;
  color: #fff;
}
.ent-title-inner {
  width: min(var(--gs-main-width, 1250px), calc(100% - 32px));
  margin: 0 auto;
  padding: 28px 0 24px;
  box-sizing: border-box;
}
.ent-title h1 {
  margin: 0;
  font-size: 30px;
  line-height: 1.15;
  font-weight: 800;
}
.ent-title p {
  margin: 8px 0 0;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.88);
}
.ent-main {
  width: 100%;
  margin: 16px 0 32px;
}
.ent-filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 16px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #D7E6FF;
  border-radius: 12px;
  box-sizing: border-box;
}
.ent-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.ent-field > span {
  flex: 0 0 64px;
  color: #334155;
  font-size: 13px;
  text-align: right;
}
.ent-field input {
  flex: 1 1 auto;
  min-width: 0;
  height: 32px;
  box-sizing: border-box;
  border: 1px solid #D7DEE8;
  border-radius: 4px;
  padding: 0 8px;
  color: #1f2937;
  font-size: 13px;
  outline: none;
  background: #fff;
}
.ent-field input:focus {
  border-color: #0165FF;
}
.ent-field :deep(.gs-select) {
  width: auto;
  flex: 1 1 auto;
}
.ent-dates :deep(.gs-date) {
  height: 32px;
  min-width: 0;
  padding: 0 4px;
  font-size: 12px;
}
.ent-dates em {
  flex: 0 0 auto;
  color: #64748b;
  font-style: normal;
  font-size: 12px;
}
.ent-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.ent-btn {
  height: 32px;
  min-width: 72px;
  border: 0;
  border-radius: 4px;
  background: #0165FF;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}
.ent-btn.is-plain {
  background: #fff;
  color: #0165FF;
  border: 1px solid #0165FF;
}
.ent-table-wrap {
  position: relative;
  min-height: 180px;
}
.ent-table {
  background: #fff;
  border: 1px solid #D7E6FF;
  border-radius: 12px;
  overflow: hidden;
}
.ent-head,
.ent-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1.4fr 1fr 1fr 72px;
  gap: 10px;
  align-items: center;
  padding: 0 18px;
}
.ent-head {
  height: 54px;
  background: linear-gradient(180deg, #EAF3FF 0%, #D7E6FF 100%);
  color: #0165FF;
  font-size: 15px;
  font-weight: 700;
}
.ent-row {
  min-height: 64px;
  border-top: 1px solid #EEF3F8;
  font-size: 14px;
  color: #334155;
}
.ent-row:nth-child(even) {
  background: #F8FBFF;
}
.ent-row:hover {
  background: #EEF5FF;
}
.ent-row > span,
.ent-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ent-name {
  font-size: 15px;
  font-weight: 700;
  color: #0B1F3A;
}
.ent-act button {
  border: 0;
  background: transparent;
  color: #0165FF;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
}
.ent-pager {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
@media (max-width: 1249px) {
  .ent-main {
    width: min(var(--gs-main-width, 1250px), calc(100% - 32px));
    margin-left: auto;
    margin-right: auto;
  }
}
@media (max-width: 960px) {
  .ent-page .gs-page-hero {
    margin-bottom: 12px;
  }
  .ent-main {
    width: calc(100% - 24px);
    margin-left: auto;
    margin-right: auto;
    padding-bottom: 24px;
  }
  .ent-filters {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px;
    margin-bottom: 12px;
  }
  .ent-filters.is-collapsed .is-more {
    display: none;
  }
  .ent-field {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }
  .ent-field > span {
    flex: none;
    text-align: left;
    font-size: 12px;
    color: #64748b;
  }
  .ent-field input,
  .ent-field :deep(.gs-select),
  .ent-field :deep(.gs-select .el-input__inner),
  .ent-dates :deep(.gs-date) {
    height: 34px;
    font-size: 13px;
  }
  .ent-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 2px;
  }
  .ent-actions .ent-btn {
    width: 100%;
    min-width: 0;
    height: 34px;
    font-size: 13px;
  }
  .ent-more-btn {
    grid-column: 1 / -1;
  }
  .ent-table {
    background: transparent;
    border: 0;
    border-radius: 0;
    overflow: visible;
  }
  .ent-head {
    display: none;
  }
  .ent-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .ent-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    min-height: 0;
    padding: 12px;
    background: #fff;
    border: 1px solid #E4E7ED;
    border-radius: 10px;
    font-size: 12px;
  }
  .ent-row:nth-child(even),
  .ent-row:hover {
    background: #fff;
  }
  .ent-row > span,
  .ent-name {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }
  .ent-name {
    font-size: 15px;
    line-height: 1.35;
    margin-bottom: 2px;
  }
  .ent-row > span[data-label]::before {
    content: attr(data-label) '：';
    color: #7B889C;
    font-weight: 400;
    margin-right: 2px;
  }
  .ent-act {
    align-self: flex-end;
    margin-top: 2px;
  }
  .ent-act button {
    height: 28px;
    padding: 0 12px;
    border-radius: 6px;
    background: #0165FF;
    color: #fff;
    font-size: 12px;
  }
}
</style>
